import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useRouter, Href } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { CircleAlert, XIcon } from 'lucide-react-native';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

import { headerItem } from '@/components/HeaderActions';
import { LinkedInquiryBanner } from '@/components/LinkedInquiryBanner';
import { NEW_INQUIRY_TITLE, NewInquiryHeader } from '@/components/NewInquiryHeader';
import { SurgeonAttachmentReminderModal } from '@/components/SurgeonAttachmentReminderModal';
import { AdditionalExpertsSection } from '@/components/form/AdditionalExpertsSection';
import { AnonymousCheckbox } from '@/components/form/AnonymousCheckbox';
import { CancerTypeSelect } from '@/components/form/CancerTypeSelect';
import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { ExpertSelect } from '@/components/form/ExpertSelect';
import { FileUploadField } from '@/components/form/FileUploadField';
import { MessageField } from '@/components/form/MessageField';
import { NoPHIField } from '@/components/form/NoPHIField';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import TransitionScreen from '@/components/transitionScreen';
import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import {
  Popover,
  PopoverArrow,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from '@/components/ui/popover';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { SPECIALTY_BADGE_CLASS, SPECIALTY_BADGE_TEXT_CLASS } from '@/constants/badges';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { needsVerificationToSubmitInquiry, useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useInquiryForm } from '@/hooks/useInquiryForm';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useProductSupportPostSubmit } from '@/hooks/useProductSupportPostSubmit';
import { useSingleValueSearchParams } from '@/hooks/useSingleValueSearchParams';
import { referenceMessageBodyAtom } from '@/services/atoms/inquiry';
import {
  getAlgoliaExpertByIdQueryOptions,
  getAlgoliaExpertsInfiniteQueryOptions,
} from '@/services/getAlgoliaExperts';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { getUserPublicV2QueryOptions } from '@/services/getUserPublicV2';
import {
  communityShowAuthModalAtom,
  productSupportPrefillMessageAtom,
  userIdAtom,
} from '@/services/globalState';
import {
  resolveInquiryCancerTypePrefill,
  setInquiryCancerTypePrefill,
  useInquiryCancerTypePrefill,
} from '@/services/stores/inquiryCancerTypePrefillStore';
import { segmentClient } from '@/util/analytics';
import { trackLibraryChatInquirySubmitted } from '@/util/analyticsEvents';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { resolveSelectedExperts } from '@/util/inquiryExperts';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import {
  savePendingInquiryDraft,
  loadPendingInquiryDraft,
  hasPendingInquiryDraft,
} from '@/util/pendingInquiryDraft';
import { isAskAnonymously } from '@/util/userFilters';
import { isSurgeon } from '@/utils/expertHelpers';

const SCREEN_COLUMN = getScreenColumnClasses();

const INQUIRY_PARAM_KEYS = [
  'id',
  'focusId',
  'draftId',
  'referenceConversationId',
  'referenceCancerFocusId',
] as const;

/**
 * Inquiry Form Component
 *
 * Inquiry form that allows users to submit inquiries without selecting a specific expert.
 * Users can optionally select up to 3 experts, or let Primum match them to an appropriate expert.
 *
 * **Expert Selection:**
 * - Progressive disclosure via `enableExpertSelection` checkbox (up to 3 experts)
 * - Experts filtered by cancer type; selection cleared when cancer type changes
 * - The chosen experts are saved in the draft and restored when it is opened
 *
 * **Draft Behavior:**
 * - Opens with cancer type: experts auto-fetched, checkbox enabled immediately (even while loading)
 * - Expert query uses `cancerFocusId` from form state
 * - Loads/restores form fields (cancer type, message, PII, anonymous, experts); auto-saves on change
 * - Routes to `/inquiry?draftId=...` when opening draft
 *
 * **Presentation:**
 * - In the Inquiry tab the composer is a top-level page with no back button, and on web it is a
 *   top-level nav destination. Opened over another screen, such as a chat, it is a nested screen
 *   that keeps its back button.
 * - Deleting a draft on a top-level page empties the form where it is, since there is nowhere to
 *   go back to and opening another composer would stack a second screen. Elsewhere it goes back.
 * - On native the root must stay a bare view that only fills the screen, with the scroll view
 *   straight after the header. iOS collapses the large title only against a scroll view in the
 *   screen's first descendant chain, and a root with a background or alignment is drawn as a
 *   real view rather than flattened away, which hides the scroll view and leaves the title stuck
 *   over the form.
 */
export default function NewInquiry({ isTabRoot = false }: { isTabRoot?: boolean }) {
  const { replace, push, setParams } = useRouter();

  const replaceRef = useRef(replace);

  useEffect(() => {
    replaceRef.current = replace;
  }, [replace]);

  const setPrefillMessage = useSetAtom(productSupportPrefillMessageAtom);

  const isModProductSupportSuccessEnabled = useFeatureFlag(
    FEATURE_FLAGS.MOD_PRODUCT_SUPPORT_SUCCESS_SCREEN
  );

  const isInquiryMultiExpertNotesEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_MULTI_EXPERT_NOTES);

  const { navigateBack } = useBackNavigation(ROUTES.DIRECTORY);

  const isTopLevelPage = isTabRoot || isWeb;

  const { isPrimumUser } = useCognitoGroups();

  const insets = useSafeAreaInsets();

  const [showModal, setShowModal] = useState(false);

  const [showReminderModal, setShowReminderModal] = useState(false);

  const { id, focusId, draftId, referenceConversationId, referenceCancerFocusId } =
    useSingleValueSearchParams(INQUIRY_PARAM_KEYS);

  const [referenceMessageBody, setReferenceMessageBody] = useAtom(referenceMessageBodyAtom);

  const askCancerTypePrefill = useInquiryCancerTypePrefill();

  const cancerFocusQuery = useQuery(getCancerTypesV2QueryOptions());

  /**
   * The Ask handoff this screen took, held locally because the screen outlives
   * it: on native `/inquiry` is also a tab that stays mounted, so anything left
   * in the store would prefill and attribute every later inquiry. Taken once,
   * and dropped on submit or when the screen loses focus.
   */
  const [askHandoff, setAskHandoff] = useState<{
    conversationId: string;
    cancerFocusId: string;
    hint: string | undefined;
  } | null>(null);

  const askCancerTypeHint = askHandoff?.hint;

  const isFocused = useIsFocused();

  const setShowAuthModal = useSetAtom(communityShowAuthModalAtom);

  const { requestAccess } = useAbilityGate();

  const userId = useAtomValue(userIdAtom);

  const isLoggedIn = !!userId;

  const isLoggedOut = !isLoggedIn;

  const isSubmittingToSelf = isLoggedIn && userId === id;

  const formDisabled = isSubmittingToSelf;

  const hasCheckedPendingRef = useRef(false);

  const currentUserQuery = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const ability = useAbility();

  const needsNpiVerification = isLoggedIn && needsVerificationToSubmitInquiry(ability);

  const isSubmitDenied = isLoggedIn && !ability.can('submit', 'Inquiry');

  const expertByIdQuery = useQuery(getAlgoliaExpertByIdQueryOptions(id));

  const expert = expertByIdQuery.data ?? undefined;

  const isLoading = expertByIdQuery.isLoading;

  const expertIsAnonymous = isAskAnonymously(expert?.name);

  const publicExpertQuery = useQuery({
    ...getUserPublicV2QueryOptions(id || ''),
    enabled: !!id,
  });

  const expertData = publicExpertQuery.data;

  const expertCancerFocuses = expertData?.cancerFocusIds || [];

  const isExpertAway = expertData?.status === '2';

  const currentUserSummary = currentUserQuery.data
    ? {
        firstName: currentUserQuery.data.firstName ?? undefined,
        lastName: currentUserQuery.data.lastName ?? undefined,
        email: currentUserQuery.data.email || undefined,
      }
    : undefined;

  const selectedCancerFocusId = useMemo(() => {
    const singleCancerFocusId =
      expertCancerFocuses.length === 1 ? (expertCancerFocuses[0]?.id?.toString() ?? '') : '';

    return focusId || referenceCancerFocusId || singleCancerFocusId || '';
  }, [expertCancerFocuses, focusId, referenceCancerFocusId]);

  const { captureSubmissionSnapshot, resolvePostSubmitSuccess } = useProductSupportPostSubmit({
    isModProductSupportSuccessEnabled,
    source: 'new_inquiry',
  });

  const renderMobileExpertCard = () => {
    if (expertIsAnonymous) {
      return (
        <VStack className="w-full md:hidden">
          <Card variant="filled" className="w-full md:hidden">
            <VStack>
              <HStack className="mb-2 items-center">
                <Avatar className="mr-3">
                  <AvatarFallbackText>Ask Anonymously</AvatarFallbackText>
                  <AvatarImage source={expert?.avatarSrc ? { uri: expert.avatarSrc } : undefined} />
                </Avatar>
                <Heading size="h2-xl" className="mb-2 flex-shrink text-typography-900">
                  Ask Anonymously
                </Heading>
              </HStack>
              <Text className="text-wrap text-typography-700">
                Submit your inquiries anonymously.
              </Text>
            </VStack>
          </Card>
        </VStack>
      );
    }

    if (!expert) {
      return null;
    }

    return (
      <Card variant="filled" className="w-full md:hidden" data-component="expert-profile">
        <VStack>
          <HStack className="items-center">
            <Avatar className="mr-3">
              <Skeleton className="h-12 w-12 rounded-full" isLoaded={!isLoading}>
                <AvatarFallbackText>{expert?.name || ''}</AvatarFallbackText>
                <AvatarImage source={expert?.avatarSrc ? { uri: expert.avatarSrc } : undefined} />
              </Skeleton>
            </Avatar>
            <VStack className="flex-1 items-baseline">
              <SkeletonText className="h-6 w-1/2" isLoaded={!isLoading}>
                <Heading size="h2-xl" className="mb-2 flex-shrink text-typography-900">
                  {expert?.name ?? ''}
                </Heading>
              </SkeletonText>
              {expert?.speciality && (
                <Badge action="info" size="lg" className={twJoin('lg:mb-0', SPECIALTY_BADGE_CLASS)}>
                  <BadgeText className={SPECIALTY_BADGE_TEXT_CLASS}>
                    {expert?.speciality || ''}
                  </BadgeText>
                </Badge>
              )}
            </VStack>
          </HStack>

          <Divider className="my-4" />

          <VStack className="gap-2">
            {expert?.institution && (
              <HStack className="items-start gap-2">
                <Text className="text-sm text-typography-700">{expert?.institution || ''}</Text>
              </HStack>
            )}

            {expert?.title && (
              <HStack className="items-start gap-2">
                <SkeletonText className="h-4 w-full" isLoaded={!isLoading}>
                  <Text className="text-wrap text-sm text-typography-700">
                    {expert?.title || ''}
                  </Text>
                </SkeletonText>
              </HStack>
            )}
          </VStack>
        </VStack>
      </Card>
    );
  };

  const renderExpertSelectionSection = ({ isDraftContext }: { isDraftContext: boolean }) => {
    if (expert && !expertIsAnonymous) {
      return (
        <VStack className="mb-4 mt-4 web:mb-9 web:mt-0">
          <AdditionalExpertsSection
            control={control}
            errors={errors}
            setValue={setValue}
            isDisabled={formDisabled}
            cancerFocusId={cancerFocusId}
            enableExpertSelection={formValues.enableExpertSelection || false}
            filteredExperts={filteredExperts}
            isLoadingFilteredExperts={isLoadingFilteredExperts}
            cancerTypeLabel={selectedCancerTypeLabel}
            currentUser={currentUserSummary}
            {...(isDraftContext
              ? {
                  isFetchingFilteredExperts,
                  isDraft: !!draftId,
                }
              : undefined)}
          />
        </VStack>
      );
    }

    if (!expert && cancerFocusId) {
      return (
        <VStack className="mb-4 mt-4 web:mb-9 web:mt-0">
          <ExpertSelect
            control={control}
            errors={errors}
            setValue={setValue}
            isDisabled={formDisabled}
            cancerFocusId={cancerFocusId}
            enableExpertSelection={formValues.enableExpertSelection || false}
            filteredExperts={filteredExperts}
            isLoadingFilteredExperts={isLoadingFilteredExperts}
            cancerTypeLabel={selectedCancerTypeLabel}
            currentUser={currentUserSummary}
            {...(isDraftContext
              ? {
                  isFetchingFilteredExperts,
                  isDraft: !!draftId,
                }
              : undefined)}
          />
        </VStack>
      );
    }

    return null;
  };

  const navigateToInquirySuccess = useCallback(
    (
      expertIds: number[],
      cancerFocusId: number,
      productSupportPrefill?: { productId?: string; prefillMessage?: string }
    ) => {
      const firstExpertId = expertIds[0] || id || '';

      const params = new URLSearchParams();

      if (firstExpertId) {
        params.set('id', String(firstExpertId));
      }

      params.set('focusId', String(cancerFocusId));

      if (productSupportPrefill) {
        params.set('productSupport', '1');

        if (productSupportPrefill.productId) {
          params.set('productId', productSupportPrefill.productId);
        }

        if (productSupportPrefill.prefillMessage) {
          setPrefillMessage(productSupportPrefill.prefillMessage);
        }
      }

      replaceRef.current(`/directory/inquiry/success?${params.toString()}`);
    },
    [id, setPrefillMessage]
  );

  const handleInquirySuccess = useCallback(
    (expertIds: number[], cancerFocusId: number, inquiryId?: number) => {
      if (askHandoff && !draftId) {
        const prefilledId = askHandoff.cancerFocusId ? Number(askHandoff.cancerFocusId) : null;

        trackLibraryChatInquirySubmitted({
          conversation_id: askHandoff.conversationId,
          inquiry_id: inquiryId ?? null,
          prefilled_cancer_type_id: prefilledId,
          submitted_cancer_type_id: Number(cancerFocusId),
          cancer_type_changed: prefilledId !== null && prefilledId !== Number(cancerFocusId),
        });
      }

      setAskHandoff(null);

      const processInquirySuccess = async () => {
        const { successState } = await resolvePostSubmitSuccess({ inquiryId });

        const productSupportPrefill = successState.showSuccess
          ? {
              productId: successState.productId,
              prefillMessage: successState.prefillMessage,
            }
          : undefined;

        navigateToInquirySuccess(expertIds, cancerFocusId, productSupportPrefill);
      };

      processInquirySuccess().catch(() => undefined);
    },
    [askHandoff, draftId, navigateToInquirySuccess, resolvePostSubmitSuccess]
  );

  const {
    control,
    errors,
    formValues,
    isValid,
    setValue,
    submitInquiry,
    deleteDraft,
    disableDraftSaving,
    resetToEmptyForm,
    isSubmitting,
    isDeleteDisabled,
    isDeleting,
    submissionStatus,
  } = useInquiryForm({
    expertId: undefined,
    currentDraftId: draftId,
    selectedCancerFocusId,
    referenceConversationId,
    onSuccess: handleInquirySuccess,
  });

  const isMaintenanceMode = useMaintenanceMode();

  const capturePostSubmitSnapshot = useCallback(() => {
    captureSubmissionSnapshot({
      message: formValues.message,
      cancerFocusId: formValues.cancerFocusId,
    });
  }, [captureSubmissionSnapshot, formValues.cancerFocusId, formValues.message]);

  useEffect(() => {
    if (isLoggedIn && !needsNpiVerification && !hasCheckedPendingRef.current) {
      hasCheckedPendingRef.current = true;

      setShowAuthModal(false);

      const checkPendingDraftAndRedirect = async () => {
        const hasPending = await hasPendingInquiryDraft();

        if (hasPending) {
          disableDraftSaving();

          replace('/inquiry/redirect' as Href);
        }
      };

      checkPendingDraftAndRedirect();
    }
  }, [isLoggedIn, needsNpiVerification, replace, disableDraftSaving, setShowAuthModal]);

  useEffect(() => {
    if (isLoggedOut) {
      const prefillFromLocalStorage = async () => {
        const pendingDraft = await loadPendingInquiryDraft();

        if (pendingDraft) {
          if (pendingDraft.cancerFocusId) {
            setValue('cancerFocusId', pendingDraft.cancerFocusId, { shouldDirty: true });
          }

          if (pendingDraft.message) {
            setValue('message', pendingDraft.message, { shouldDirty: true });
          }

          if (pendingDraft.noPII) {
            setValue('noPII', pendingDraft.noPII, { shouldDirty: true });
          }

          if (pendingDraft.anonymous) {
            setValue('anonymous', pendingDraft.anonymous, { shouldDirty: true });
          }
        }
      };

      prefillFromLocalStorage();
    }
  }, [isLoggedOut, setValue]);

  const appliedReferenceMessageRef = useRef('');

  /**
   * Takes a handed-off question into the message once. Waits for focus for the same reason as
   * the cancer type below: the Inquiry tab stays mounted under a stacked composer, and without
   * the wait it would copy the question into its own form. Remembers what it applied, so a
   * screen regaining focus does not overwrite what the reader has typed since; the hand-off
   * itself is cleared only when a composer unmounts, which the tab never does.
   */
  useEffect(() => {
    const message = referenceMessageBody.trim();

    const handoffKey = message ? `${referenceConversationId ?? ''}:${message}` : '';

    if (!handoffKey) {
      appliedReferenceMessageRef.current = '';

      return;
    }

    if (!isFocused || draftId || appliedReferenceMessageRef.current === handoffKey) {
      return;
    }

    appliedReferenceMessageRef.current = handoffKey;

    setValue('message', message, { shouldDirty: false });
  }, [draftId, isFocused, referenceMessageBody, referenceConversationId, setValue]);

  /**
   * Clears the atom on unmount so it doesn't leak to future navigations.
   * Not cleared in the prefill effect: component can remount when URL params load
   * (e.g. referenceConversationId); a second mount would see an empty atom and fail to prefill.
   */
  useEffect(() => {
    return () => {
      setReferenceMessageBody('');

      setInquiryCancerTypePrefill(null);
    };
  }, [setReferenceMessageBody]);

  /**
   * Takes an Ask handoff's cancer type once the cancer types have loaded, then
   * empties the store. It replaces whatever the field held, as the handoff's
   * question replaces the message: a matched id is selected, and a label alone
   * empties the field so its hint shows. The expert picks tied to the old
   * cancer type go with it. Waits for focus, because a mounted tab sees the
   * store change before the navigation lands and its params still name the
   * draft it last showed. A draft carries its own cancer type, so a handoff
   * that lands on one is dropped.
   */
  useEffect(() => {
    const cancerTypeLookup = cancerFocusQuery.data?.cancerTypeLookup;

    if (!isFocused || !askCancerTypePrefill || !cancerTypeLookup) {
      return;
    }

    setInquiryCancerTypePrefill(null);

    if (draftId) {
      setAskHandoff(null);

      return;
    }

    const resolved = resolveInquiryCancerTypePrefill(askCancerTypePrefill, cancerTypeLookup);

    if (resolved.cancerFocusId || resolved.hint) {
      setValue('cancerFocusId', resolved.cancerFocusId, { shouldDirty: false });

      setValue('expertIds', []);

      setValue('expertNotes', {});

      setValue('enableExpertSelection', false);
    }

    setAskHandoff({ conversationId: askCancerTypePrefill.conversationId, ...resolved });
  }, [askCancerTypePrefill, cancerFocusQuery.data, draftId, isFocused, setValue]);

  useEffect(() => {
    if (!isFocused) {
      setAskHandoff(null);
    }
  }, [isFocused]);

  useEffect(() => {
    segmentClient.track(ANALYTICS_EVENTS.ASK_AN_EXPERT_FORM_LOADED, {
      loggedIn: isLoggedIn,
    });
  }, [isLoggedIn]);

  const isInvalidForm = !isValid;

  const isExpertUnavailable = !!(id && (isExpertAway || isSubmittingToSelf));

  const isSubmitVisuallyDisabled =
    isInvalidForm || isMaintenanceMode || isExpertUnavailable || isSubmitDenied;

  const isSubmissionInProgress = isSubmitting || !!submissionStatus;

  const isSubmitDisabled = isSubmitVisuallyDisabled || isSubmissionInProgress || formDisabled;

  const hasAttachment = useMemo(() => {
    return formValues.files && formValues.files.length > 0;
  }, [formValues.files]);

  /**
   * Read from the form-wide watch, never a `useWatch` for this one field declared here. Opening a
   * draft that is already cached loads it in the form hook's first effect, before a watch declared
   * this late has subscribed, so that watch stayed empty and the Experts section, the experts query
   * and the cancer type label all disappeared while the select showed the type.
   */
  const cancerFocusId = formValues.cancerFocusId ?? '';

  const saveToLocalStorageDebounced = useDebouncedCallback(
    useCallback(() => {
      if (isLoggedOut) {
        savePendingInquiryDraft({
          cancerFocusId: formValues.cancerFocusId || '',
          message: formValues.message || '',
          noPII: formValues.noPII || false,
          anonymous: formValues.anonymous || false,
        });
      }
    }, [
      isLoggedOut,
      formValues.cancerFocusId,
      formValues.message,
      formValues.noPII,
      formValues.anonymous,
    ]),
    1000,
    { leading: false, trailing: true }
  );

  useEffect(() => {
    if (isLoggedOut) {
      saveToLocalStorageDebounced();
    }

    return () => saveToLocalStorageDebounced.cancel();
  }, [
    isLoggedOut,
    formValues.cancerFocusId,
    formValues.message,
    formValues.noPII,
    formValues.anonymous,
    saveToLocalStorageDebounced,
  ]);

  const selectedCancerTypeLabel = useMemo(() => {
    if (!cancerFocusId || !cancerFocusQuery.data) {
      return '';
    }

    const cancerType = cancerFocusQuery.data.cancerTypeLookup.get(Number(cancerFocusId));

    return cancerType?.label || '';
  }, [cancerFocusId, cancerFocusQuery.data]);

  const {
    data: filteredExpertsData,
    isLoading: isLoadingFilteredExperts,
    isFetching: isFetchingFilteredExperts,
  } = useInfiniteQuery({
    ...getAlgoliaExpertsInfiniteQueryOptions(
      {
        searchQuery: '',
        focusId: cancerFocusId,
      },
      { hitsPerPage: 100, maxPages: 1 }
    ),
    enabled: !!cancerFocusId,
    placeholderData: (previousData) => previousData,
  });

  const allFilteredExperts = filteredExpertsData?.pages?.flatMap((page) => page.hits) || [];

  const filteredExperts = useMemo(() => {
    return allFilteredExperts
      .filter((expert) => !id || expert.id !== id)
      .filter((expert) => expert.id !== userId)
      .filter((expert) => !isAskAnonymously(expert.name))
      .sort((a, b) => {
        const nameA = a.nameWithoutDoctor || a.name;

        const nameB = b.nameWithoutDoctor || b.name;

        return nameA.localeCompare(nameB);
      });
  }, [allFilteredExperts, id, userId]);

  const hasAnySurgeonExpert = useMemo(() => {
    const allSelectedExperts = [
      expert,
      ...(formValues.expertIds?.map((expertId) =>
        allFilteredExperts.find((candidate) => candidate.id === String(expertId))
      ) || []),
    ].filter(Boolean);

    return allSelectedExperts.some((selected) => isSurgeon(selected?.speciality));
  }, [expert, formValues.expertIds, allFilteredExperts]);

  const selectedExperts = useMemo(() => {
    return resolveSelectedExperts({
      expert,
      expertIds: formValues.expertIds,
      allExperts: allFilteredExperts,
    });
  }, [allFilteredExperts, expert, formValues.expertIds]);

  const continueRegularSubmission = useCallback(async () => {
    if (!isValid) {
      await submitInquiry();

      return;
    }

    if (hasAnySurgeonExpert && !hasAttachment && !showReminderModal) {
      setShowReminderModal(true);

      return;
    }

    await submitInquiry();
  }, [hasAnySurgeonExpert, hasAttachment, isValid, showReminderModal, submitInquiry]);

  const handleSubmitInquiry = async () => {
    segmentClient.track(ANALYTICS_EVENTS.ASK_AN_EXPERT_FORM_SUBMITTED, {
      loggedIn: isLoggedIn,
    });

    if (isLoggedOut) {
      await savePendingInquiryDraft({
        cancerFocusId: formValues.cancerFocusId || '',
        message: formValues.message || '',
        noPII: formValues.noPII || false,
        anonymous: formValues.anonymous || false,
      });

      requestAccess();

      return;
    }

    capturePostSubmitSnapshot();

    await continueRegularSubmission();
  };

  const handleSubmitInquiryFromModal = () => {
    setShowReminderModal(false);

    capturePostSubmitSnapshot();

    submitInquiry();
  };

  const [triggerFilePicker, setTriggerFilePicker] = useState(false);

  const handleAttachFilesFromModal = () => {
    setTriggerFilePicker(true);
  };

  const handleTriggerComplete = () => {
    setTriggerFilePicker(false);
  };

  const handleDraftDeleted = () => {
    if (!isTopLevelPage) {
      navigateBack();

      return;
    }

    resetToEmptyForm();

    setAskHandoff(null);

    if (draftId) {
      setParams({ draftId: undefined });
    }
  };

  const deleteDraftItem = headerItem.deleteDraft(() => {
    segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_STARTED, {
      draftId,
    });

    setShowModal(true);
  }, isDeleteDisabled);

  if (isSubmissionInProgress) {
    return <TransitionScreen message={submissionStatus || 'Submitting your inquiry ...'} />;
  }

  return (
    <VStack
      className={isWeb ? 'w-full flex-1 items-center justify-center bg-background-50' : 'flex-1'}>
      {isWeb ? (
        <ScrollView
          className="w-full flex-1"
          contentContainerClassName="min-h-full"
          showsVerticalScrollIndicator={false}>
          <VStack className="w-full">
            <VStack className="w-full bg-background-0">
              <VStack className="mx-auto w-full max-w-screen-xl px-5 md:px-10">
                <Header title={NEW_INQUIRY_TITLE} rightItems={[deleteDraftItem]} />
              </VStack>
            </VStack>

            <VStack
              className="mx-auto w-full max-w-screen-xl flex-1 px-5 web:pb-10 web:pt-10 md:px-10"
              style={{ paddingBottom: insets.bottom }}>
              <VStack className="mx-auto w-full max-w-4xl flex-1 rounded-lg bg-background-0 web:mb-10 web:px-10 web:pt-10">
                <VStack className="py-5 web:md:pt-0">
                  {isPrimumUser && referenceConversationId ? (
                    <LinkedInquiryBanner
                      conversationId={referenceConversationId}
                      loggedInUserId={userId}
                      label="Linked from"
                      alwaysShowView
                      onView={(id: string) =>
                        push((isWeb ? `/inbox?id=${id}` : `/inbox/chat/${id}`) as Href)
                      }
                      containerClassName="mb-4 w-full items-start justify-start rounded bg-background-0 px-3 py-2"
                      textClassName="text-sm text-typography-700"
                    />
                  ) : null}

                  <Text size="sm" className="mb-4 font-normal leading-normal text-typography-700">
                    Tell us about your case or question and we will route it to the right
                    subspecialist.
                  </Text>

                  {isExpertAway && id && (
                    <HStack className="mb-4 w-full items-start bg-info-0 p-4">
                      <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-info-800" />
                      <VStack className="ml-3 w-0 flex-1">
                        <Heading size="lg" className="text-info-800">
                          Your expert is currently unavailable
                        </Heading>
                      </VStack>
                    </HStack>
                  )}

                  {isSubmittingToSelf && id && (
                    <HStack className="mb-4 w-full items-start bg-warning-0 p-4">
                      <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-warning-800" />
                      <VStack className="ml-3 w-0 flex-1">
                        <Heading size="lg" className="text-warning-800">
                          Cannot submit inquiry to yourself
                        </Heading>
                        <Text className="text-warning-700">
                          You cannot send an inquiry to your own profile. Please select a different
                          expert.
                        </Text>
                      </VStack>
                    </HStack>
                  )}

                  {renderMobileExpertCard()}

                  <VStack className="flex-shrink pb-5 pt-5 md:pt-0">
                    {/* Required legend */}
                    <HStack className="mb-4">
                      <Text size="xs" className="text-typography-500">
                        * Required
                      </Text>
                    </HStack>

                    <VStack className="web:mb-9">
                      <CancerTypeSelect
                        control={control}
                        errors={errors}
                        isDisabled={formDisabled}
                        expertCancerFocuses={expertCancerFocuses}
                        hint={askCancerTypeHint}
                        onCancerTypeChange={() => {
                          setValue('expertIds', []);

                          setValue('expertNotes', {});

                          setValue('enableExpertSelection', false);
                        }}
                      />
                    </VStack>

                    {renderExpertSelectionSection({ isDraftContext: true })}

                    <VStack className="mb-3">
                      <MessageField
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        isDisabled={formDisabled}
                        previewExperts={isInquiryMultiExpertNotesEnabled ? selectedExperts : []}
                        isReferenceInquiry={Boolean(referenceConversationId)}
                      />
                    </VStack>

                    <VStack className="mb-0 gap-0">
                      <FileUploadField
                        control={control}
                        errors={errors}
                        isDisabled={formDisabled}
                        isExpertSurgeon={hasAnySurgeonExpert}
                        triggerFilePicker={triggerFilePicker}
                        onTriggerComplete={handleTriggerComplete}
                      />

                      <NoPHIField
                        control={control}
                        errors={errors}
                        isDisabled={formDisabled}
                        hasFiles={!!(formValues.files && formValues.files.length > 0)}
                      />
                    </VStack>

                    {!id && (
                      <VStack className={twJoin('mb-4 web:mb-9', !hasAttachment && 'web:mt-9')}>
                        <AnonymousCheckbox
                          control={control}
                          errors={errors}
                          isDisabled={formDisabled}
                        />
                      </VStack>
                    )}

                    <Divider className="mb-4 web:mb-9" />

                    <ConfirmationCheckbox
                      control={control}
                      errors={errors}
                      fieldName="noPII"
                      variant="pii"
                      isDisabled={formDisabled}
                      className="mb-6"
                    />

                    <Button
                      onPress={handleSubmitInquiry}
                      isDisabled={
                        isSubmissionInProgress ||
                        formDisabled ||
                        isExpertAway ||
                        isSubmitVisuallyDisabled
                      }
                      className={twJoin(
                        'web:w-fit',
                        isSubmitVisuallyDisabled ? 'opacity-50' : '',
                        'px-20'
                      )}
                      testID="submit-inquiry-button">
                      <ButtonText>Submit</ButtonText>
                    </Button>
                  </VStack>
                </VStack>
              </VStack>
            </VStack>
          </VStack>
        </ScrollView>
      ) : (
        <>
          <NewInquiryHeader isTabRoot={isTabRoot} deleteDraftItem={deleteDraftItem} />

          <KeyboardAwareScrollView
            mode="layout"
            contentInsetAdjustmentBehavior="automatic"
            bottomOffset={18}
            className="mx-auto w-full max-w-4xl flex-1"
            contentContainerClassName={twJoin('py-5', SCREEN_COLUMN.content)}
            showsVerticalScrollIndicator={false}
            keyboardDismissMode="interactive"
            keyboardShouldPersistTaps="handled">
            {isPrimumUser && referenceConversationId ? (
              <LinkedInquiryBanner
                conversationId={referenceConversationId}
                loggedInUserId={userId}
                label="Linked from"
                alwaysShowView
                onView={(id: string) =>
                  push((isWeb ? `/inbox?id=${id}` : `/inbox/chat/${id}`) as Href)
                }
                containerClassName="mb-4 w-full items-start justify-start rounded bg-background-0"
                textClassName="text-sm text-typography-700"
              />
            ) : null}

            <Text size="sm" className="mb-2 font-normal leading-normal text-typography-700">
              Tell us about your case or question and we will route it to the right subspecialist.
            </Text>

            {isExpertAway && id && (
              <HStack className="mb-4 w-full items-start bg-info-0 p-4">
                <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-info-800" />
                <VStack className="ml-3 w-0 flex-1">
                  <Heading size="lg" className="text-info-800">
                    Your expert is currently unavailable
                  </Heading>
                </VStack>
              </HStack>
            )}

            {isSubmittingToSelf && id && (
              <HStack className="mb-4 w-full items-start bg-warning-0 p-4">
                <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-warning-800" />
                <VStack className="ml-3 w-0 flex-1">
                  <Heading size="lg" className="text-warning-800">
                    Cannot submit inquiry to yourself
                  </Heading>
                  <Text className="text-warning-700">
                    You cannot send an inquiry to your own profile. Please select a different
                    expert.
                  </Text>
                </VStack>
              </HStack>
            )}

            {renderMobileExpertCard()}

            <VStack className="flex-shrink pb-5 pt-5 md:pt-0">
              {/* Required legend */}
              <HStack className="mb-4">
                <Text size="xs" className="text-typography-500">
                  * Required
                </Text>
              </HStack>

              <VStack className="web:mb-9">
                <CancerTypeSelect
                  control={control}
                  errors={errors}
                  isDisabled={formDisabled}
                  expertCancerFocuses={expertCancerFocuses}
                  hint={askCancerTypeHint}
                  onCancerTypeChange={() => {
                    setValue('expertIds', []);

                    setValue('expertNotes', {});

                    setValue('enableExpertSelection', false);
                  }}
                />
              </VStack>

              {renderExpertSelectionSection({ isDraftContext: false })}

              <VStack className="mb-3">
                <MessageField
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  isDisabled={formDisabled}
                  previewExperts={isInquiryMultiExpertNotesEnabled ? selectedExperts : []}
                  isReferenceInquiry={Boolean(referenceConversationId)}
                />
              </VStack>

              <VStack className="mb-0 gap-0">
                <FileUploadField
                  control={control}
                  errors={errors}
                  isDisabled={formDisabled}
                  isExpertSurgeon={hasAnySurgeonExpert}
                  triggerFilePicker={triggerFilePicker}
                  onTriggerComplete={handleTriggerComplete}
                />

                <NoPHIField
                  control={control}
                  errors={errors}
                  isDisabled={formDisabled}
                  hasFiles={!!(formValues.files && formValues.files.length > 0)}
                />
              </VStack>

              {!id && (
                <VStack className={twJoin('mb-4 web:mb-9', !hasAttachment && 'web:mt-9')}>
                  <AnonymousCheckbox control={control} errors={errors} isDisabled={formDisabled} />
                </VStack>
              )}

              <Divider className="mb-4 web:mb-9" />

              <ConfirmationCheckbox
                control={control}
                errors={errors}
                fieldName="noPII"
                variant="pii"
                isDisabled={formDisabled}
                className="mb-6"
              />

              <Button
                onPress={handleSubmitInquiry}
                isDisabled={isSubmitDisabled}
                className={twJoin(
                  'web:w-fit',
                  isSubmitVisuallyDisabled ? 'opacity-50' : '',
                  'px-20'
                )}>
                <ButtonText>Submit</ButtonText>
              </Button>
            </VStack>
          </KeyboardAwareScrollView>
        </>
      )}

      {isWeb && needsNpiVerification ? (
        <View
          className="absolute left-0 top-0 h-full w-full"
          onStartShouldSetResponder={() => {
            requestAccess();

            return false;
          }}
        />
      ) : null}

      <Modal
        isOpen={showModal}
        onClose={() => {
          segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

          setShowModal(false);
        }}
        size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader className="pb-5">
            <Heading size="h2-xl">Delete draft</Heading>
            <ModalCloseButton
              onPress={() => {
                segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

                setShowModal(false);
              }}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody className="pb-6">
            <Text className="text-typography-700">Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-3 pt-0">
            <Button
              disabled={isDeleting}
              className={twJoin('w-full', isDeleting && 'opacity-50')}
              onPress={() => {
                setShowModal(false);

                deleteDraft(handleDraftDeleted);
              }}>
              <ButtonText>Delete draft</ButtonText>
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => {
                segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

                setShowModal(false);
              }}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SurgeonAttachmentReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSubmitInquiry={handleSubmitInquiryFromModal}
        onAttachFiles={handleAttachFilesFromModal}
        expertId={id}
        hasAttachment={hasAttachment}
      />
    </VStack>
  );
}
